import { cn, Stack, Tooltip, TooltipContent, TooltipTrigger } from 'component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useChartZoom } from '@/components/Charts/components/ChartZoom';
import { GRAPHS_WITH_CUSTOM_BENCHMARKS } from '@/components/Charts/constants';
import { GRAPH_TYPE, useChartType } from '@/components/Charts/hooks/useChartType';
import {
  INDICATOR_LOADING_STATE,
  IndicatorProvider,
  useHasIndicatorData,
} from '@/components/IndicatorCard/IndicatorContextProvider';
import { NCCard, NCCardAnalysisTypeToggle } from '@/components/NCCard/NCCard';
import { useActivePlotGroup } from '@/components/PlotGroups/PlotGroupsContextProvider';
import { useBenchmarkSelection } from '@/hooks/useBenchmarkSelection';

import { ChartEditDropdown } from '../Charts/components/ChartEditDropdown';
import { CustomBenchmarkDialog } from '../Charts/components/CustomBenchmarkDialog';
import { GraphTileProps } from '../IndicatorCard/GraphTile';
import { GetGraphTileByType, IndicatorCardProps } from '../IndicatorCard/IndicatorCard';
import { Icon } from '../widgets/a/Icon/Icon';

export const DetailsCard = (props: IndicatorCardProps) => {
  const activePlotGroup = useActivePlotGroup();
  const plotGroupKey = JSON.stringify({ id: activePlotGroup.id, filters: activePlotGroup.filters });
  return (
    <IndicatorProvider
      key={plotGroupKey}
      defaultState={{
        statsData: false,
        graphData: INDICATOR_LOADING_STATE,
      }}
    >
      <DetailsCardContent {...props} />
    </IndicatorProvider>
  );
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const DetailsCardContent = ({ headerProps, graphTileType, graphTileProps, styles }: IndicatorCardProps) => {
  const { t } = useTranslation();
  const hasCardDataToDisplay = useHasIndicatorData();
  const { selectedGraphType, setSelectedGraphType } = useChartType();

  const { zoom, setZoom } = useChartZoom();

  const [selectedBenchmark, setSelectedBenchmark] = useBenchmarkSelection(graphTileType as string);
  const [isBenchmarkDialogOpen, setIsBenchmarkDialogOpen] = useState(false);

  const supportsCustomBenchmarks = Boolean(graphTileType && GRAPHS_WITH_CUSTOM_BENCHMARKS.has(graphTileType));

  const graphProps = {
    ...graphTileProps,
    headerProps: {
      ...graphTileProps?.headerProps,
      title: '',
      hideChartHeader: true,
    },
  } as GraphTileProps;

  const showEmpty = graphTileProps?.showEmpty ?? false;

  const showZoom = headerProps?.showZoom && selectedGraphType !== GRAPH_TYPE.BAR;

  if (!hasCardDataToDisplay && !showEmpty) {
    return null;
  }

  return (
    <NCCard className={cn('p-8', styles?.root)}>
      <Stack
        className={cn(
          'w-full flex flex-col items-center justify-items-center gap-4 lg:grid lg:grid-cols-[1fr_auto_1fr]',
          headerProps?.showZoom ? 'lg:justify-items-between' : 'lg:justify-items-start',
          (headerProps?.showZoom || headerProps?.showGraphType) &&
            !headerProps?.showToggle &&
            'lg:grid-cols-[auto_1fr]',
        )}
      >
        {headerProps?.title && (
          <Stack spacing={2} className='justify-self-start'>
            <span className='typography-overline uppercase'>{t('shared.ncaDetail.details.historicDevelopment')}</span>
            <span className='typography-h4'>
              {headerProps?.title}
              {headerProps?.headerTooltip && (
                <Tooltip>
                  <TooltipTrigger className='items-center'>
                    <Icon icon='ri-information-line' className='ml-1 text-active-54 typography-body1' />
                  </TooltipTrigger>
                  <TooltipContent className='whitespace-pre-wrap'>{headerProps?.headerTooltip}</TooltipContent>
                </Tooltip>
              )}
            </span>
          </Stack>
        )}
        {headerProps?.showToggle && <NCCardAnalysisTypeToggle data-testid='analysis-type-toggle' />}
        <Stack spacing={4} direction='row' className='justify-self-end' centerMain>
          {(showZoom || headerProps?.showGraphType) && (
            <ChartEditDropdown
              graphType={headerProps?.showGraphType ? selectedGraphType : undefined}
              setGraphType={headerProps?.showGraphType ? setSelectedGraphType : undefined}
              indicator={graphTileType}
              zoom={showZoom ? zoom : undefined}
              setZoom={showZoom ? setZoom : undefined}
              selectedBenchmark={supportsCustomBenchmarks ? selectedBenchmark : undefined}
              setSelectedBenchmark={supportsCustomBenchmarks ? setSelectedBenchmark : undefined}
              onOpenBenchmarkDialog={supportsCustomBenchmarks ? () => setIsBenchmarkDialogOpen(true) : undefined}
            />
          )}
        </Stack>
      </Stack>
      <Stack className='items-center sm:items-stretch' center>
        {graphTileType &&
          GetGraphTileByType(graphTileType, graphProps, zoom, setZoom, selectedGraphType, selectedBenchmark)}
      </Stack>
      {graphTileType && (
        <CustomBenchmarkDialog
          isOpen={isBenchmarkDialogOpen}
          onClose={() => setIsBenchmarkDialogOpen(false)}
          indicatorType={graphTileType}
        />
      )}
    </NCCard>
  );
};
